import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/EntityManager";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError, api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { KeyRound, Smartphone, Mail, Building2, Archive, ReceiptText, Coins } from "lucide-react";
import { toast } from "sonner";
import { BRANCHES, loadBranches } from "@/lib/branches";

const OFFICES = ["Chennai", "Tada", "Hyderabad", "Jaipur"];

export default function Settings() {
  const { user, changePassword, updatePhone, updateEmail, updateBranch } = useAuth();

  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPass, setSavingPass] = useState(false);

  const [phone, setPhone] = useState(user?.phone || "");
  const [savingPhone, setSavingPhone] = useState(false);

  const [otpMobile, setOtpMobile] = useState("");
  const [savingOtpMobile, setSavingOtpMobile] = useState(false);

  const [email, setEmail] = useState(user?.email || "");
  const [otpEmail, setOtpEmail] = useState(user?.otp_email || "");
  const [savingEmail, setSavingEmail] = useState(false);

  const [branch, setBranch] = useState(user?.office || "Chennai");
  const [savingBranch, setSavingBranch] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    if (isAdmin) api.get("/settings/otp-mobile").then((r) => setOtpMobile(r.data.mobile || "")).catch(() => {});
    if (isAdmin) api.get("/settings/accounts-email").then((r) => setAccountsEmail(r.data.email || "")).catch(() => {});
  }, [isAdmin]);

  const [accountsEmail, setAccountsEmail] = useState("");
  const [savingAccountsEmail, setSavingAccountsEmail] = useState(false);
  const submitAccountsEmail = async (e) => {
    e.preventDefault();
    setSavingAccountsEmail(true);
    try {
      const r = await api.put("/settings/accounts-email", { email: accountsEmail });
      setAccountsEmail(r.data.email);
      toast.success(r.data.email ? "Accounts email saved — the monthly charges sheet will go there" : "Cleared — the monthly charges sheet will go to the admin email");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingAccountsEmail(false);
    }
  };

  const submitOtpMobile = async (e) => {
    e.preventDefault();
    setSavingOtpMobile(true);
    try {
      const r = await api.put("/settings/otp-mobile", { mobile: otpMobile });
      setOtpMobile(r.data.mobile);
      toast.success("Login OTP mobile updated — all login OTPs will now go to this number");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSavingOtpMobile(false);
    }
  };

  const [backupMonth, setBackupMonth] = useState(new Date().toISOString().slice(0, 7));
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const emailBackup = async () => {
    setEmailing(true);
    try {
      const res = await api.post("/reports/monthly-backup/email", { month: backupMonth });
      toast.success(`Backup for ${backupMonth} emailed to ${res.data.to}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Backup email failed");
    } finally {
      setEmailing(false);
    }
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/reports/monthly-backup", { params: { month: backupMonth }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ntc-backup-${backupMonth}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup for ${backupMonth} downloaded`);
    } catch (err) {
      toast.error("Backup download failed");
    } finally {
      setDownloading(false);
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSavingPass(true);
    try {
      await changePassword(cur, next);
      toast.success("Password changed successfully");
      setCur("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPass(false);
    }
  };

  const submitPhone = async (e) => {
    e.preventDefault();
    setSavingPhone(true);
    try {
      await updatePhone(phone);
      toast.success("Mobile number updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPhone(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await updateEmail(email, otpEmail);
      toast.success("Email ID updated — login OTPs will go to the OTP email (or this address if blank)");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingEmail(false);
    }
  };

  const submitBranch = async (e) => {
    e.preventDefault();
    setSavingBranch(true);
    try {
      await updateBranch(branch);
      toast.success("Branch updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingBranch(false);
    }
  };

  return (
    <div>
      <PageHeader title="Account Settings" subtitle="Manage your login credentials, email, branch and mobile number." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                <KeyRound className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">Change Password</h3>
                <p className="text-xs text-muted-foreground">Update the password for {user?.email}</p>
              </div>
            </div>
            <form onSubmit={submitPassword} className="space-y-4">
              <div className="grid gap-1.5">
                <Label>Current password</Label>
                <Input type="password" data-testid="current-password" value={cur} onChange={(e) => setCur(e.target.value)} required placeholder="••••••••" />
              </div>
              <div className="grid gap-1.5">
                <Label>New password</Label>
                <Input type="password" data-testid="new-password" value={next} onChange={(e) => setNext(e.target.value)} required placeholder="Min 6 characters" />
              </div>
              <div className="grid gap-1.5">
                <Label>Confirm new password</Label>
                <Input type="password" data-testid="confirm-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="Re-enter new password" />
              </div>
              <Button type="submit" disabled={savingPass} data-testid="save-password-btn">
                {savingPass ? "Saving…" : "Update Password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                <Smartphone className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">Mobile Number</h3>
                <p className="text-xs text-muted-foreground">Used for OTP login and password reset</p>
              </div>
            </div>
            <form onSubmit={submitPhone} className="space-y-4">
              <div className="grid gap-1.5">
                <Label>Mobile number</Label>
                <Input data-testid="settings-phone" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="10-digit mobile number" />
              </div>
              <Button type="submit" disabled={savingPhone} data-testid="save-phone-btn">
                {savingPhone ? "Saving…" : "Update Mobile Number"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <KeyRound className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Login OTP Mobile</h3>
                  <p className="text-xs text-muted-foreground">All login OTP SMS codes go to this number</p>
                </div>
              </div>
              <form onSubmit={submitOtpMobile} className="space-y-4">
                <div className="grid gap-1.5">
                  <Label>OTP mobile number</Label>
                  <Input data-testid="settings-otp-mobile" value={otpMobile} onChange={(e) => setOtpMobile(e.target.value)} required placeholder="10-digit mobile number" />
                </div>
                <Button type="submit" disabled={savingOtpMobile} data-testid="save-otp-mobile-btn">
                  {savingOtpMobile ? "Saving…" : "Update OTP Mobile"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <Mail className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Accounts Email — Monthly Charges Sheet</h3>
                  <p className="text-xs text-muted-foreground">The 1st-of-month extra-charges sheet (decisions + admin edits) goes here; leave blank to send to the admin email</p>
                </div>
              </div>
              <form onSubmit={submitAccountsEmail} className="space-y-4">
                <div className="grid gap-1.5">
                  <Label>Accounts email address</Label>
                  <Input data-testid="settings-accounts-email" type="email" value={accountsEmail} onChange={(e) => setAccountsEmail(e.target.value)} placeholder="accounts@ntclogistic.in (blank = admin email)" />
                </div>
                <Button type="submit" disabled={savingAccountsEmail} data-testid="save-accounts-email-btn">
                  {savingAccountsEmail ? "Saving…" : "Save Accounts Email"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                <Mail className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">Email ID</h3>
                <p className="text-xs text-muted-foreground">Login OTPs and password-reset codes are sent here</p>
              </div>
            </div>
            <form onSubmit={submitEmail} className="space-y-4">
              <div className="grid gap-1.5">
                <Label>Email ID</Label>
                <Input type="email" data-testid="settings-email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
              </div>
              <div className="grid gap-1.5">
                <Label>OTP Email (where login codes are sent)</Label>
                <Input type="email" data-testid="settings-otp-email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} placeholder="Leave blank to use Email ID" />
                <p className="text-xs text-muted-foreground">Login OTP codes are sent to this address. Leave blank to use your Email ID.</p>
              </div>
              <Button type="submit" disabled={savingEmail} data-testid="save-email-btn">
                {savingEmail ? "Saving…" : "Update Email ID"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardContent className="p-6">
            <div className="mb-4 flex items-center gap-2">
              <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                <Building2 className="h-4 w-4" />
              </div>
              <div>
                <h3 className="font-display text-lg font-bold">Branch</h3>
                <p className="text-xs text-muted-foreground">
                  {isAdmin ? "Your branch is selected at login; admin sees all branches" : "Your branch controls which records you can see"}
                </p>
              </div>
            </div>
            {isAdmin ? (
              <form onSubmit={submitBranch} className="space-y-4">
                <div className="grid gap-1.5">
                  <Label>Branch</Label>
                  <Select value={branch} onValueChange={setBranch}>
                    <SelectTrigger data-testid="settings-branch"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {OFFICES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={savingBranch} data-testid="save-branch-btn">
                  {savingBranch ? "Saving…" : "Update Branch"}
                </Button>
              </form>
            ) : (
              <div className="space-y-3">
                <div className="grid gap-1.5">
                  <Label>Branch</Label>
                  <Input value={user?.office || "Chennai"} disabled data-testid="settings-branch-readonly" />
                </div>
                <p className="text-xs text-muted-foreground">Only an admin can change your branch (Staff Logins page).</p>
              </div>
            )}
          </CardContent>
        </Card>

        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <Archive className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Monthly Backup</h3>
                  <p className="text-xs text-muted-foreground">Download all key registers for a month as one ZIP of Excel/CSV files</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="grid gap-1.5">
                  <Label>Month</Label>
                  <Input type="month" value={backupMonth} onChange={(e) => setBackupMonth(e.target.value)} data-testid="backup-month-input" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Includes: Consignments, Bills, Money Receipts, Debit Vouchers, Vehicle Hire Challans, Expenses, Salaries, Attendance.
                  The previous month's backup is also emailed to you automatically on the 1st of every month.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={downloadBackup} disabled={downloading || !backupMonth} data-testid="download-backup-btn">
                    {downloading ? "Preparing…" : "Download Backup"}
                  </Button>
                  <Button variant="outline" onClick={emailBackup} disabled={emailing || !backupMonth} data-testid="email-backup-btn">
                    {emailing ? "Sending…" : "Email Backup Now"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <ReceiptText className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Proof Alert Limit</h3>
                  <p className="text-xs text-muted-foreground">Vouchers/expenses at or above this amount without a bill photo show a "Proof missing" alert</p>
                </div>
              </div>
              <ProofLimitForm />
            </CardContent>
          </Card>
        )}
        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <Smartphone className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Branch Contact Book</h3>
                  <p className="text-xs text-muted-foreground">WhatsApp number per branch — alerts and follow-ups go straight to the right person</p>
                </div>
              </div>
              <BranchContactBook />
            </CardContent>
          </Card>
        )}
        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <Coins className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Charge Presets</h3>
                  <p className="text-xs text-muted-foreground">Commonly used charge rates — staff pick them with one tap on the bill form instead of retyping</p>
                </div>
              </div>
              <ChargePresetsForm />
            </CardContent>
          </Card>
        )}
        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <ReceiptText className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Extra Charge Quick Picks</h3>
                  <p className="text-xs text-muted-foreground">One-tap reason chips shown under the Extra Charges Reason box on the Vehicle Hire Challan</p>
                </div>
              </div>
              <ChargeReasonPicksForm />
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-9 w-9 place-items-center rounded-md bg-primary/10 text-primary">
                  <Coins className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold">Yearly Extra-Charges Cap</h3>
                  <p className="text-xs text-muted-foreground">Branches crossing their cap are flagged red on the FY Summary, print and monthly email</p>
                </div>
              </div>
              <ChargesCapForm />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

const CHARGE_TYPE_LABELS = [
  ["loading", "Loading Charge"],
  ["unloading", "Unloading Charge"],
  ["lph", "Loading Point Halting Charge"],
  ["halting", "Unloading Point Halting Charge"],
  ["labour", "Labour Union Charge"],
  ["crane", "Crane Charge"],
  ["vreturn", "Vehicle Return Charge"],
  ["edelivery", "Extra Delivery Charge"],
  ["packing", "Packing Charge"],
];

function ChargePresetsForm() {
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState([]);
  useEffect(() => {
    api.get("/charge-presets").then((r) => {
      const byType = {};
      (r.data || []).forEach((p) => {
        byType[p.charge_type] = byType[p.charge_type] ? `${byType[p.charge_type]}, ${p.amount}` : String(p.amount);
      });
      setVals(byType);
    }).catch(() => {});
    api.get("/reports/charge-usage").then((r) => setUsage(r.data || [])).catch(() => {});
  }, []);
  const labelFor = (key) => (CHARGE_TYPE_LABELS.find(([k]) => k === key) || [])[1] || key;
  const hasPreset = (key, amount) =>
    String(vals[key] || "").split(",").map((s) => Number(s.trim())).includes(Number(amount));
  const addFromUsage = (key, amount) => {
    if (hasPreset(key, amount)) return;
    setVals((v) => ({ ...v, [key]: v[key] ? `${v[key]}, ${amount}` : String(amount) }));
    toast.info(`₹${amount} added to ${labelFor(key)} — press Save Presets to keep it`);
  };
  const save = async () => {
    setSaving(true);
    try {
      const presets = [];
      CHARGE_TYPE_LABELS.forEach(([key]) => {
        String(vals[key] || "")
          .split(",")
          .map((s) => Number(s.trim()))
          .filter((n) => n > 0)
          .forEach((amount) => presets.push({ charge_type: key, amount }));
      });
      await api.put("/charge-presets", { presets });
      toast.success("Charge presets saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3">
      {CHARGE_TYPE_LABELS.map(([key, label]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="w-56 shrink-0 text-sm font-medium">{label}</span>
          <Input
            className="h-9"
            placeholder="e.g. 1000, 2000, 3000"
            value={vals[key] ?? ""}
            onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.value }))}
            data-testid={`charge-preset-${key}`}
          />
        </div>
      ))}
      <p className="text-xs text-muted-foreground">Enter amounts separated by commas. They appear as one-tap chips under each charge box on the bill form.</p>
      <Button size="sm" disabled={saving} onClick={save} data-testid="save-charge-presets-btn">
        {saving ? "Saving…" : "Save Presets"}
      </Button>
      {usage.length > 0 && (
        <div className="mt-4 rounded-md border border-border" data-testid="charge-usage-report">
          <div className="border-b border-border bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-600">
            Most used charge rates (from saved bills)
          </div>
          <div className="max-h-64 overflow-y-auto">
            {usage.slice(0, 20).map((u, i) => (
              <div key={i} className="flex items-center gap-2 border-b border-border/60 px-3 py-1.5 text-sm last:border-0" data-testid={`charge-usage-row-${i}`}>
                <span className="w-56 shrink-0 text-muted-foreground">{labelFor(u.charge_type)}</span>
                <span className="w-24 font-semibold tabular-nums">₹{Number(u.amount).toLocaleString("en-IN")}</span>
                <span className="text-xs text-muted-foreground">used {u.count} {u.count === 1 ? "time" : "times"}</span>
                <span className="ml-auto">
                  {hasPreset(u.charge_type, u.amount) ? (
                    <span className="text-xs font-semibold text-emerald-600">Preset ✓</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => addFromUsage(u.charge_type, u.amount)}
                      className="rounded-full border border-slate-300 px-2 py-0.5 text-xs font-semibold text-slate-600 hover:border-primary hover:text-primary"
                      data-testid={`add-usage-preset-${u.charge_type}-${u.amount}`}
                    >
                      + Add as preset
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BranchContactBook() {
  const [contacts, setContacts] = useState({});
  const [savingBranch, setSavingBranch] = useState("");
  useEffect(() => {
    loadBranches().then(() => api.get("/branch-contacts").then((r) => setContacts(r.data || {})).catch(() => {}));
  }, []);
  const save = async (branch) => {
    setSavingBranch(branch);
    try {
      const r = await api.put("/branch-contacts", { branch, whatsapp: contacts[branch] || "" });
      setContacts((c) => ({ ...c, [branch]: r.data.whatsapp }));
      toast.success(`${branch} WhatsApp saved`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingBranch("");
    }
  };
  return (
    <div className="space-y-3">
      {BRANCHES.map((b) => (
        <div key={b} className="flex items-center gap-2">
          <span className="w-24 shrink-0 text-sm font-medium">{b}</span>
          <Input
            className="h-9"
            placeholder="e.g. 919876543210 (with country code)"
            value={contacts[b] ?? ""}
            onChange={(e) => setContacts((c) => ({ ...c, [b]: e.target.value }))}
            data-testid={`branch-contact-${b.toLowerCase().replace(/\s+/g, "-")}`}
          />
          <Button size="sm" variant="outline" className="h-9" disabled={savingBranch === b} onClick={() => save(b)} data-testid={`save-branch-contact-${b.toLowerCase().replace(/\s+/g, "-")}`}>
            {savingBranch === b ? "…" : "Save"}
          </Button>
        </div>
      ))}
    </div>
  );
}

function ChargesCapForm() {
  const [limit, setLimit] = useState("");
  const [branchLims, setBranchLims] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/charges-cap").then((r) => {
      setLimit(r.data?.limit ? String(r.data.limit) : "");
      setBranchLims(Object.fromEntries(Object.entries(r.data?.branches || {}).map(([k, v]) => [k, String(v)])));
    }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const branches = Object.fromEntries(Object.entries(branchLims).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      await api.put("/settings/charges-cap", { limit: Number(limit) || 0, branches });
      toast.success("Yearly extra-charges caps saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3">
      <div className="grid gap-1.5">
        <Label className="text-xs">Common yearly cap (₹) per branch, April–March. 0 or blank = off</Label>
        <Input type="number" min="0" placeholder="e.g. 50000" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="charges-cap-input" />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Per-branch caps (₹) — blank = use the common cap above</Label>
        <div className="grid grid-cols-2 gap-2">
          {BRANCHES.map((b) => (
            <div key={b} className="flex items-center gap-2">
              <span className="w-20 shrink-0 text-xs font-medium">{b}</span>
              <Input type="number" min="0" placeholder="—" value={branchLims[b] || ""} onChange={(e) => setBranchLims((p) => ({ ...p, [b]: e.target.value }))} data-testid={`charges-cap-${b.toLowerCase()}`} />
            </div>
          ))}
        </div>
      </div>
      <Button size="sm" disabled={saving} onClick={save} data-testid="save-charges-cap-btn">
        {saving ? "Saving…" : "Save Caps"}
      </Button>
    </div>
  );
}

function ChargeReasonPicksForm() {
  const [text, setText] = useState("");
  const [limit, setLimit] = useState("");
  const [branchLims, setBranchLims] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/settings/charge-reasons").then((r) => {
      setText((r.data?.reasons || []).join("\n"));
      setLimit(r.data?.limit ? String(r.data.limit) : "");
      setBranchLims(Object.fromEntries(Object.entries(r.data?.branches || {}).map(([k, v]) => [k, String(v)])));
    }).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const reasons = text.split("\n").map((s) => s.trim()).filter(Boolean);
      const branches = Object.fromEntries(Object.entries(branchLims).filter(([, v]) => Number(v) > 0).map(([k, v]) => [k, Number(v)]));
      const r = await api.post("/settings/charge-reasons", { reasons, limit: Number(limit) || 0, branches });
      setText((r.data?.reasons || []).join("\n"));
      toast.success("Quick-pick reasons & approval limits saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3">
      <textarea
        className="min-h-[120px] w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
        placeholder={"One reason per line, e.g.\nNight unloading\nOverweight\nBorder challan"}
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="charge-reason-picks-input"
      />
      <p className="text-xs text-muted-foreground">One reason per line (max 20). Leave empty to restore the default list.</p>
      <div className="grid gap-1.5">
        <Label className="text-xs">Approval limit (₹) — admin approval needed when a single challan's extra charges cross this. 0 or blank = off</Label>
        <Input type="number" min="0" placeholder="e.g. 2000" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="charge-limit-input" />
      </div>
      <div className="grid gap-1.5">
        <Label className="text-xs">Per-branch limits (₹) — blank = use the common limit above</Label>
        <div className="grid grid-cols-2 gap-2">
          {BRANCHES.map((b) => (
            <div key={b} className="flex items-center gap-2">
              <span className="w-20 shrink-0 text-xs font-medium">{b}</span>
              <Input type="number" min="0" placeholder="—" value={branchLims[b] || ""} onChange={(e) => setBranchLims((p) => ({ ...p, [b]: e.target.value }))} data-testid={`charge-limit-${b.toLowerCase()}`} />
            </div>
          ))}
        </div>
      </div>
      <Button size="sm" disabled={saving} onClick={save} data-testid="save-charge-reason-picks-btn">
        {saving ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

function ProofLimitForm() {
  const [limit, setLimit] = useState("");
  const [padDays, setPadDays] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/app-settings").then((r) => {
      setLimit(String(r.data?.proof_alert_limit ?? 5000));
      setPadDays(String(r.data?.lost_pad_days ?? 7));
    }).catch(() => { setLimit("5000"); setPadDays("7"); });
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/app-settings", { proof_alert_limit: Number(limit) || 0, lost_pad_days: Number(padDays) || 7 });
      toast.success("Alert settings updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-1.5">
        <Label>Proof alert limit (₹)</Label>
        <Input type="number" min="0" data-testid="proof-limit-input" value={limit} onChange={(e) => setLimit(e.target.value)} required placeholder="5000" />
      </div>
      <div className="grid gap-1.5">
        <Label>Lost pad alert after (days)</Label>
        <Input type="number" min="1" data-testid="lost-pad-days-input" value={padDays} onChange={(e) => setPadDays(e.target.value)} required placeholder="7" />
        <p className="text-xs text-muted-foreground">Blank pad serial ranges still unused after this many days show a dashboard alert.</p>
      </div>
      <Button type="submit" disabled={saving} data-testid="save-proof-limit-btn">
        {saving ? "Saving…" : "Update Settings"}
      </Button>
    </form>
  );
}
